package com.writerslab

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.remember
import org.jetbrains.compose.web.attributes.AttrsScope
import org.jetbrains.compose.web.dom.ElementScope
import org.jetbrains.compose.web.dom.TagElement
import org.w3c.dom.HTMLElement
import org.w3c.dom.events.Event

/**
 * Thin Compose-HTML wrappers around the Material Web custom elements we use.
 * They are intentionally minimal: they only expose the props/events we
 * actually need, everything else stays at default. The host page styles them
 * via CSS variables (see `md-...` rules in `index.html`).
 */

/**
 * `<md-dialog>` — modal with focus trap, ESC dismissal, scroll lock.
 * The dialog opens via the `open` attribute and emits a `close` event when
 * dismissed (ESC, scrim click, or `dialog.close()`).
 */
@Composable
fun MdDialog(
    open: Boolean,
    onClose: () -> Unit,
    headline: String,
    headlineTrailing: (@Composable ElementScope<HTMLElement>.() -> Unit)? = null,
    actions: (@Composable ElementScope<HTMLElement>.() -> Unit)? = null,
    content: @Composable ElementScope<HTMLElement>.() -> Unit,
) {
    remember { loadMdDialog(); Unit }
    TagElement<HTMLElement>(
        tagName = "md-dialog",
        applyAttrs = {
            if (open) attr("open", "")
            ref { node ->
                val handler: (Event) -> Unit = { onClose() }
                node.addEventListener("close", handler)
                onDispose { node.removeEventListener("close", handler) }
            }
        },
        content = {
            TagElement<HTMLElement>(
                tagName = "div",
                applyAttrs = {
                    attr("slot", "headline")
                    style {
                        property("display", "flex")
                        property("align-items", "center")
                        property("justify-content", "space-between")
                        property("gap", "12px")
                        property("width", "100%")
                    }
                },
                content = {
                    org.jetbrains.compose.web.dom.Span({}) {
                        org.jetbrains.compose.web.dom.Text(headline)
                    }
                    if (headlineTrailing != null) {
                        TagElement<HTMLElement>(
                            tagName = "div",
                            applyAttrs = {
                                style {
                                    property("display", "flex")
                                    property("flex-shrink", "0")
                                }
                            },
                            content = headlineTrailing,
                        )
                    }
                },
            )
            TagElement<HTMLElement>(
                tagName = "div",
                applyAttrs = { attr("slot", "content") },
                content = content,
            )
            if (actions != null) {
                TagElement<HTMLElement>(
                    tagName = "div",
                    applyAttrs = { attr("slot", "actions") },
                    content = actions,
                )
            }
        },
    )
}

/**
 * `<md-outlined-text-field>` — proper Material outlined text field with a
 * floating label and an error state.
 */
@Composable
fun MdOutlinedTextField(
    value: String,
    onValueChange: (String) -> Unit,
    label: String,
    placeholder: String? = null,
    disabled: Boolean = false,
    error: Boolean = false,
    errorText: String? = null,
    onSubmit: (() -> Unit)? = null,
    attrs: (AttrsScope<HTMLElement>.() -> Unit)? = null,
) {
    remember { loadMdOutlinedTextField(); Unit }
    TagElement<HTMLElement>(
        tagName = "md-outlined-text-field",
        applyAttrs = {
            attr("label", label)
            attr("value", value)
            if (placeholder != null) attr("placeholder", placeholder)
            if (disabled) attr("disabled", "")
            if (error) attr("error", "")
            if (error && errorText != null) attr("error-text", errorText)
            attrs?.invoke(this)
            ref { node ->
                val inputHandler: (Event) -> Unit = { ev ->
                    val v = (ev.target.asDynamic().value as? String).orEmpty()
                    if (v != value) onValueChange(v)
                }
                node.addEventListener("input", inputHandler)
                val keydownHandler: ((Event) -> Unit)? = onSubmit?.let { submit ->
                    { ev ->
                        if (ev.asDynamic().key == "Enter") {
                            ev.preventDefault()
                            submit()
                        }
                    }
                }
                if (keydownHandler != null) node.addEventListener("keydown", keydownHandler)
                onDispose {
                    node.removeEventListener("input", inputHandler)
                    if (keydownHandler != null) node.removeEventListener("keydown", keydownHandler)
                }
            }
        },
        content = null,
    )
}

/**
 * `<md-linear-progress>` — Material linear progress, with indeterminate
 * mode when [value] is `null`.
 */
@Composable
fun MdLinearProgress(value: Float?) {
    remember { loadMdLinearProgress(); Unit }
    TagElement<HTMLElement>(
        tagName = "md-linear-progress",
        applyAttrs = {
            if (value == null) {
                attr("indeterminate", "")
            } else {
                attr("value", value.coerceIn(0f, 1f).toString())
            }
        },
        content = null,
    )
}

/** `<md-circular-progress>` — indeterminate by default. */
@Composable
fun MdCircularProgress(value: Float? = null, fourColor: Boolean = false) {
    remember { loadMdCircularProgress(); Unit }
    TagElement<HTMLElement>(
        tagName = "md-circular-progress",
        applyAttrs = {
            if (value == null) {
                attr("indeterminate", "")
            } else {
                attr("value", value.coerceIn(0f, 1f).toString())
            }
            if (fourColor) attr("four-color", "")
        },
        content = null,
    )
}
